import {
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { CalendarDays, ChevronLeft, ChevronRight, X } from "lucide-react";
import { cn } from "../../lib/cn";

export type DatePickerMode = "date" | "datetime";

export interface DatePickerProps {
  /** "YYYY-MM-DD" in date mode, "YYYY-MM-DDTHH:mm" in datetime mode, or "" for empty. */
  value: string;
  onChange: (value: string) => void;
  mode?: DatePickerMode;
  label?: string;
  placeholder?: string;
  helperText?: string;
  id?: string;
  className?: string;
}

const WEEKDAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const pad = (n: number) => String(n).padStart(2, "0");

/**
 * Local-time parsing, deliberately hand-rolled.
 *
 * `new Date("2026-08-28")` is parsed by the spec as UTC midnight, so west of Greenwich it
 * renders as the 27th — an off-by-one-day bug that only shows up for some users in some
 * timezones. Splitting the string and using the Date(y, m, d) constructor keeps everything in
 * the viewer's own local time, which is what a calendar should show.
 */
function parse(value: string): {
  date: Date | null;
  hour: number;
  minute: number;
} {
  if (!value) return { date: null, hour: 9, minute: 0 };
  const [datePart, timePart] = value.split("T");
  const [y, m, d] = (datePart ?? "").split("-").map(Number);
  if (!y || !m || !d) return { date: null, hour: 9, minute: 0 };
  const [hh, mm] = (timePart ?? "").split(":").map(Number);
  return {
    date: new Date(y, m - 1, d),
    hour: Number.isFinite(hh) ? hh! : 9,
    minute: Number.isFinite(mm) ? mm! : 0,
  };
}

function serialize(
  date: Date,
  mode: DatePickerMode,
  hour: number,
  minute: number
): string {
  const ymd = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate()
  )}`;
  return mode === "datetime" ? `${ymd}T${pad(hour)}:${pad(minute)}` : ymd;
}

const sameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

/** The 42 cells of a 6-week grid, so the panel never changes height between months. */
function monthGrid(year: number, month: number): Date[] {
  const firstWeekday = new Date(year, month, 1).getDay();
  const start = new Date(year, month, 1 - firstWeekday);
  return Array.from(
    { length: 42 },
    (_, i) =>
      new Date(start.getFullYear(), start.getMonth(), start.getDate() + i)
  );
}

/**
 * A themed date / date-time picker.
 *
 * Replaces `<input type="date">`, whose popup is browser chrome that CSS cannot reach — not the
 * month grid, not the selection highlight, not the time columns. `color-scheme: dark` is the only
 * lever a native input offers, which left the one piece of the UI that ignored the app's design
 * entirely, and looked different again in Firefox and Safari.
 *
 * No date library: the arithmetic here is the Date constructor and a 42-cell loop, which is not
 * worth a dependency. Panel positioning follows the same absolute-anchor approach as Combobox.
 */
export function DatePicker({
  value,
  onChange,
  mode = "date",
  label,
  placeholder = "Select a date",
  helperText,
  id,
  className,
}: DatePickerProps) {
  const generatedId = useId();
  const fieldId = id ?? generatedId;
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  // Nullable generic so this is a MutableRefObject — the callback ref below assigns to it,
  // and useRef<T>(null) types `current` as readonly.
  const panelRef = useRef<HTMLDivElement | null>(null);
  const [anchor, setAnchor] = useState<{ top: number; left: number } | null>(
    null
  );

  const parsed = useMemo(() => parse(value), [value]);
  const today = useMemo(() => new Date(), []);

  // The month on screen. Starts at the selected date, or today when empty.
  const [view, setView] = useState(() => parsed.date ?? today);
  // Keyboard cursor, so arrow keys can move without committing a value.
  const [cursor, setCursor] = useState(() => parsed.date ?? today);

  // Reopening after the value changed elsewhere should land on the right month.
  useEffect(() => {
    if (!open) return;
    const next = parse(value).date ?? new Date();
    setView(next);
    setCursor(next);
  }, [open, value]);

  useEffect(() => {
    if (!open) return;
    function onPointerDown(event: PointerEvent) {
      if (!wrapperRef.current?.contains(event.target as Node)) setOpen(false);
    }
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [open]);

  /**
   * Positions the panel against the viewport instead of the document.
   *
   * An absolutely-positioned panel still counts toward its scroll container's overflow, so
   * opening this near the bottom of a page physically GREW the page and spawned a scrollbar —
   * the field jumped and the layout shifted underneath the click. `position: fixed` takes the
   * panel out of that calculation entirely, so opening it can never change the page's height.
   *
   * The trade is that fixed coordinates do not follow the field on scroll, so the position is
   * recomputed on scroll and resize. Measuring the panel first (rather than assuming a height)
   * is what lets it flip above the field when there is not enough room below.
   */
  useLayoutEffect(() => {
    if (!open) return;

    const place = () => {
      const trigger = triggerRef.current;
      const panel = panelRef.current;
      if (!trigger) return;

      const rect = trigger.getBoundingClientRect();
      const panelH = panel?.offsetHeight ?? 380;
      const panelW = panel?.offsetWidth ?? 304;
      const GAP = 4;
      const EDGE = 8;

      const roomBelow = window.innerHeight - rect.bottom;
      const flipUp = roomBelow < panelH + GAP && rect.top > roomBelow;

      const top = flipUp
        ? Math.max(EDGE, rect.top - panelH - GAP)
        : Math.min(rect.bottom + GAP, window.innerHeight - panelH - EDGE);

      // Keep the panel on screen when the field sits near the right edge.
      const left = Math.min(
        Math.max(EDGE, rect.left),
        window.innerWidth - panelW - EDGE
      );

      setAnchor({ top, left });
    };

    place();
    // capture:true so it also fires for scrolls inside the sticky toolbar, not just the page.
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    return () => {
      window.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
    };
  }, [open]);

  const grid = useMemo(
    () => monthGrid(view.getFullYear(), view.getMonth()),
    [view]
  );

  function commit(date: Date, hour = parsed.hour, minute = parsed.minute) {
    onChange(serialize(date, mode, hour, minute));
    if (mode === "date") {
      setOpen(false);
      triggerRef.current?.focus();
    }
  }

  function onPanelKeyDown(event: React.KeyboardEvent) {
    const step =
      event.key === "ArrowLeft"
        ? -1
        : event.key === "ArrowRight"
        ? 1
        : event.key === "ArrowUp"
        ? -7
        : event.key === "ArrowDown"
        ? 7
        : 0;

    if (step !== 0) {
      event.preventDefault();
      const next = new Date(
        cursor.getFullYear(),
        cursor.getMonth(),
        cursor.getDate() + step
      );
      setCursor(next);
      // Follow the cursor across a month boundary so it never moves out of sight.
      if (next.getMonth() !== view.getMonth()) setView(next);
      return;
    }
    if (event.key === "Enter") {
      event.preventDefault();
      commit(cursor);
      return;
    }
    if (event.key === "Escape") {
      event.preventDefault();
      setOpen(false);
      triggerRef.current?.focus();
    }
  }

  const display = parsed.date
    ? parsed.date.toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
      }) +
      (mode === "datetime"
        ? ` · ${pad(parsed.hour)}:${pad(parsed.minute)}`
        : "")
    : "";

  return (
    <div className={cn("flex flex-col gap-1.5", className)}>
      {label && (
        <label htmlFor={fieldId} className="text-sm font-medium text-text">
          {label}
        </label>
      )}

      <div ref={wrapperRef} className="relative">
        <button
          ref={triggerRef}
          id={fieldId}
          type="button"
          aria-haspopup="dialog"
          aria-expanded={open}
          onClick={() => setOpen((o) => !o)}
          className={cn(
            "flex h-10 w-full items-center justify-between gap-2 rounded-control border border-border bg-surface",
            "px-3 text-left text-sm text-text",
            "transition-colors duration-fast ease-standard",
            "focus:outline-none focus-visible:border-accent focus-visible:ring-2 focus-visible:ring-accent/20",
            open && "border-accent ring-2 ring-accent/20"
          )}
        >
          <span className={cn("truncate", !display && "text-text-muted")}>
            {display || placeholder}
          </span>
          <span className="flex shrink-0 items-center gap-1">
            {value && (
              // A span, not a button: a button inside a button is invalid HTML and browsers
              // reparent it, which breaks the trigger. Click is stopped from opening the panel.
              <span
                role="button"
                tabIndex={-1}
                aria-label="Clear date"
                onClick={(e) => {
                  e.stopPropagation();
                  onChange("");
                }}
                className="rounded-control p-0.5 text-text-muted transition-colors duration-fast hover:bg-surface-hover hover:text-text"
              >
                <X className="size-3.5" aria-hidden />
              </span>
            )}
            <CalendarDays className="size-4 text-text-muted" aria-hidden />
          </span>
        </button>

        {open && (
          <div
            role="dialog"
            aria-label="Choose a date"
            onKeyDown={onPanelKeyDown}
            tabIndex={-1}
            ref={(node) => {
              panelRef.current = node;
              node?.focus();
            }}
            style={{ top: anchor?.top ?? 0, left: anchor?.left ?? 0 }}
            className={cn(
              "fixed z-50 w-[19rem] overflow-hidden rounded-card border border-border",
              "bg-surface p-3 shadow-lg outline-none animate-content-drop",
              // Hidden until measured, so it never paints at 0,0 for a frame.
              anchor ? "visible" : "invisible"
            )}
          >
            <div className="mb-2 flex items-center justify-between">
              <button
                type="button"
                aria-label="Previous month"
                onClick={() =>
                  setView(new Date(view.getFullYear(), view.getMonth() - 1, 1))
                }
                className="inline-flex size-7 items-center justify-center rounded-control text-text-muted transition-colors duration-fast hover:bg-surface-hover hover:text-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
              >
                <ChevronLeft className="size-4" aria-hidden />
              </button>
              <span
                aria-live="polite"
                className="text-sm font-medium text-text"
              >
                {MONTHS[view.getMonth()]} {view.getFullYear()}
              </span>
              <button
                type="button"
                aria-label="Next month"
                onClick={() =>
                  setView(new Date(view.getFullYear(), view.getMonth() + 1, 1))
                }
                className="inline-flex size-7 items-center justify-center rounded-control text-text-muted transition-colors duration-fast hover:bg-surface-hover hover:text-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
              >
                <ChevronRight className="size-4" aria-hidden />
              </button>
            </div>

            <div className="mb-1 grid grid-cols-7">
              {WEEKDAYS.map((d) => (
                <span
                  key={d}
                  className="py-1 text-center text-xs font-medium text-text-muted"
                >
                  {d}
                </span>
              ))}
            </div>

            <div className="grid grid-cols-7 gap-0.5">
              {grid.map((day) => {
                const outside = day.getMonth() !== view.getMonth();
                const selected = parsed.date
                  ? sameDay(day, parsed.date)
                  : false;
                const isToday = sameDay(day, today);
                const isCursor = sameDay(day, cursor);
                return (
                  <button
                    key={day.toISOString()}
                    type="button"
                    onClick={() => commit(day)}
                    aria-current={isToday ? "date" : undefined}
                    aria-selected={selected}
                    className={cn(
                      "tnum h-8 rounded-control text-sm transition-colors duration-fast ease-standard",
                      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent",
                      outside ? "text-text-muted/50" : "text-text",
                      !selected && "hover:bg-surface-hover",
                      // Ring marks today; fill marks the selection. Both can apply at once.
                      isToday &&
                        !selected &&
                        "ring-1 ring-inset ring-accent/40",
                      isCursor && !selected && "bg-surface-hover",
                      selected &&
                        "bg-accent font-medium text-white hover:bg-accent-hover"
                    )}
                  >
                    {day.getDate()}
                  </button>
                );
              })}
            </div>

            {mode === "datetime" && (
              <div className="mt-3 flex items-center gap-2 border-t border-border pt-3">
                <label
                  htmlFor={`${fieldId}-time`}
                  className="text-xs font-medium text-text-muted"
                >
                  Time
                </label>
                <input
                  id={`${fieldId}-time`}
                  type="time"
                  value={`${pad(parsed.hour)}:${pad(parsed.minute)}`}
                  onChange={(e) => {
                    const [hh, mm] = e.target.value.split(":").map(Number);
                    const base = parsed.date ?? cursor;
                    commit(base, hh ?? 0, mm ?? 0);
                  }}
                  className="h-8 flex-1 rounded-control border border-border bg-bg px-2 text-sm text-text outline-none transition-colors duration-fast focus:border-accent focus:ring-2 focus:ring-accent/20"
                />
              </div>
            )}

            <div className="mt-3 flex items-center justify-between border-t border-border pt-3">
              <button
                type="button"
                onClick={() => {
                  onChange("");
                  setOpen(false);
                  triggerRef.current?.focus();
                }}
                className="rounded-control px-2 py-1 text-xs font-medium text-text-muted transition-colors duration-fast hover:bg-surface-hover hover:text-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
              >
                Clear
              </button>
              <button
                type="button"
                onClick={() => commit(new Date())}
                className="rounded-control px-2 py-1 text-xs font-medium text-accent transition-colors duration-fast hover:bg-accent-soft focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
              >
                Today
              </button>
            </div>
          </div>
        )}
      </div>

      {helperText && <p className="text-xs text-text-muted">{helperText}</p>}
    </div>
  );
}
